// Utility helpers for working with DICOM instances in the AI report panel.
// Kept framework-agnostic so they can be reused from any React component.

import { authService } from '../../../../platform/app/src/custom/auth/authService';

export interface DicomPatientMetadata {
  sex: string;
  age: string;
  bodyPart: string;
  modality: string;
  viewPosition: string;
}

// Normalize age from DICOM format like "034Y" -> "34"
export const normalizeDicomAge = (age?: string): string => {
  if (!age) {
    return '';
  }
  const match = age.match(/\d+/);
  return match ? match[0] : '';
};

// Map DICOM modality codes / strings to backend-expected values: MRI, XRAY, CT, ULTRASOUND
export const mapModalityForApi = (dicomModality?: string): string => {
  if (!dicomModality) {
    return '';
  }
  const code = dicomModality.toUpperCase();
  // Remove spaces, hyphens and other non-letters so we can handle "X-RAY", "X RAY", etc.
  const compact = code.replace(/[^A-Z]/g, '');

  // X-ray family: DX, CR, XR, X-RAY, X RAY, XRAY, etc.
  if (
    compact === 'DX' ||
    compact === 'CR' ||
    compact === 'XR' ||
    compact === 'XRAY' ||
    compact.startsWith('XR') ||
    compact.startsWith('XRAY')
  ) {
    return 'XRAY';
  }

  // CT: CT, CT SCAN, etc.
  if (compact === 'CT' || compact.startsWith('CT')) {
    return 'CT';
  }

  // MRI: MR, MRI, MRI BRAIN, etc.
  if (
    compact === 'MR' ||
    compact === 'MRI' ||
    compact.startsWith('MR') ||
    compact.startsWith('MRI')
  ) {
    return 'MRI';
  }

  // Ultrasound: US, USG, ULTRASOUND, etc.
  if (
    compact === 'US' ||
    compact === 'USG' ||
    compact === 'ULTRASOUND' ||
    compact.startsWith('US') ||
    compact.startsWith('ULTRASOUND')
  ) {
    return 'ULTRASOUND';
  }

  // Fallback: unknown modality -> empty string
  return '';
};

// Map DICOM modalities to template modalities (used for template selection UI only)
export const mapModalityToTemplate = (dicomModality: string): string => {
  const modalityMap: Record<string, string> = {
    DX: 'XR', // Digital Radiography -> X-Ray
    CR: 'XR', // Computed Radiography -> X-Ray
    XR: 'XR', // X-Ray
    SR: 'XR', // Structured Report -> X-Ray
    CT: 'CT', // Computed Tomography
    MR: 'MR', // Magnetic Resonance
    MRI: 'MR', // MRI -> MR
    US: 'US', // Ultrasound
  };
  return modalityMap[dicomModality] || dicomModality;
};

// Helper function to convert DICOM gender code to full text
export const formatGender = (genderCode: string): string => {
  if (!genderCode || genderCode === 'N/A') {
    return 'N/A';
  }
  const genderMap: Record<string, string> = {
    M: 'Male',
    F: 'Female',
    O: 'Other',
    MALE: 'Male',
    FEMALE: 'Female',
    OTHER: 'Other',
  };
  return genderMap[genderCode.toUpperCase()] || genderCode;
};

// Helper function to format DICOM date (YYYYMMDD) to DD-MM-YYYY
export const formatDICOMDate = (dateStr: string): string => {
  if (!dateStr || dateStr === 'N/A') {
    return 'N/A';
  }

  // Handle DICOM date format (YYYYMMDD)
  if (/^\d{8}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}-${month}-${year}`;
  }

  // Already formatted or unknown format
  return dateStr;
};

// Helper to convert date to ISO 8601 format using moment
export const convertToISODate = (dateStr: string): string => {
  if (!dateStr || dateStr === 'N/A') {
    return '';
  }

  // If already in ISO format, return as-is
  if (dateStr.includes('T') || dateStr.includes('Z')) {
    return dateStr;
  }

  // Import moment - using dynamic import for better compatibility
  let moment: any;
  try {
    // Try to use moment if available (it's used elsewhere in the codebase)
    moment = require('moment');
  } catch (e) {
    // Fallback to manual parsing if moment is not available
    console.warn('moment not available, falling back to manual parsing');
    return convertToISODateFallback(dateStr);
  }

  // Handle DICOM date format (YYYYMMDD)
  if (/^\d{8}$/.test(dateStr)) {
    const parsed = moment(dateStr, 'YYYYMMDD', true);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    }
  }

  // Try parsing common formats with moment
  const formats = ['DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD'];
  for (const format of formats) {
    const parsed = moment(dateStr, format, true);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    }
  }

  // If can't parse, return empty string
  console.warn('Unable to parse date format:', dateStr);
  return '';
};

// Fallback function for date conversion when moment is not available
const convertToISODateFallback = (dateStr: string): string => {
  // Handle DICOM date format (YYYYMMDD)
  if (/^\d{8}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${year}-${month}-${day}T00:00:00Z`;
  }

  // Handle DD-MM-YYYY format
  const ddMmYyyyMatch = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddMmYyyyMatch) {
    return `${ddMmYyyyMatch[3]}-${ddMmYyyyMatch[2]}-${ddMmYyyyMatch[1]}T00:00:00Z`;
  }

  // Handle YYYY-MM-DD format
  const yyyyMmDdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDdMatch) {
    return `${yyyyMmDdMatch[1]}-${yyyyMmDdMatch[2]}-${yyyyMmDdMatch[3]}T00:00:00Z`;
  }

  // If can't parse, return empty string
  console.warn('Unable to parse date format:', dateStr);
  return '';
};

// Infer a simplified body part label from BodyPartExamined and/or StudyDescription
export const inferBodyPart = (studyDesc: string, bodyPartExamined?: string): string => {
  const dicomBodyPart = (bodyPartExamined || '').toLowerCase();
  if (dicomBodyPart.includes('chest')) {
    return 'chest';
  }
  if (dicomBodyPart.includes('abdomen')) {
    return 'abdomen';
  }
  if (dicomBodyPart.includes('head')) {
    return 'head';
  }
  if (dicomBodyPart.includes('spine')) {
    return 'spine';
  }

  const sdLower = studyDesc.toLowerCase();
  if (sdLower.includes('chest')) {
    return 'chest';
  }
  if (sdLower.includes('abdomen')) {
    return 'abdomen';
  }
  if (sdLower.includes('head')) {
    return 'head';
  }
  if (sdLower.includes('spine')) {
    return 'spine';
  }

  return '';
};

// Infer view position from ViewPosition and/or StudyDescription
export const inferViewPosition = (studyDesc: string, viewPositionTag?: string): string => {
  let viewPosition = viewPositionTag || '';
  const sdLower = studyDesc.toLowerCase();

  if (sdLower.includes('pa')) {
    viewPosition = 'PA';
  } else if (sdLower.includes('lateral')) {
    viewPosition = 'Lateral';
  }

  return viewPosition;
};

export const extractPersonName = (pnValue: any): string => {
  if (!pnValue) {
    return 'N/A';
  }
  if (typeof pnValue === 'string') {
    return pnValue;
  }
  if (Array.isArray(pnValue) && pnValue.length > 0) {
    return pnValue[0]?.Alphabetic || pnValue[0] || 'N/A';
  }
  if (pnValue.Alphabetic) {
    return pnValue.Alphabetic;
  }
  return 'N/A';
};

// Minimal structural types to avoid `any` while staying flexible
interface DicomInstance {
  StudyDescription?: string;
  BodyPartExamined?: string;
  ViewPosition?: string;
  PatientSex?: string;
  PatientAge?: string;
  Modality?: string;
}

interface DisplaySet {
  instances?: DicomInstance[];
}

interface DisplaySetService {
  getActiveDisplaySets?: () => DisplaySet[];
}

export interface ServicesManagerLike {
  services?: {
    displaySetService?: DisplaySetService;
  };
}

// Build the DICOM-based patient metadata object used by the AI APIs
export const buildDicomMetadataForAPI = (instance: DicomInstance): DicomPatientMetadata => {
  const studyDesc = (instance?.StudyDescription as string) || '';
  const bodyPartForApi = (
    (instance?.BodyPartExamined as string) ||
    inferBodyPart(studyDesc, instance?.BodyPartExamined as string | undefined) ||
    ''
  ).toUpperCase();

  const viewPosition = inferViewPosition(studyDesc, instance?.ViewPosition as string | undefined);

  const sexTag = (instance?.PatientSex as string) || '';
  const sex = sexTag === 'F' ? 'F' : sexTag === 'M' ? 'M' : '';

  const age = normalizeDicomAge(instance?.PatientAge as string | undefined);
  const modality = mapModalityForApi(instance?.Modality as string | undefined);

  return {
    sex,
    age,
    bodyPart: bodyPartForApi,
    modality,
    viewPosition,
  };
};

// Helper to get the first active DICOM instance from servicesManager, if available.
export const getFirstActiveInstance = (
  servicesManager: ServicesManagerLike | undefined
): DicomInstance | null => {
  if (!servicesManager?.services?.displaySetService) {
    return null;
  }

  const { displaySetService } = servicesManager.services || {};
  const activeDisplaySets = displaySetService?.getActiveDisplaySets?.() || [];
  if (!activeDisplaySets.length) {
    return null;
  }

  const displaySet = activeDisplaySets[0];
  const instances = displaySet.instances || [];
  if (!instances.length) {
    return null;
  }

  return instances[0];
};

// Helper to get auth token from active session (patient: sessionStorage, RIS: localStorage)
export const getAuthToken = (): string | null => {
  return authService.getAccessToken();
};

// Helper to get auth headers for API requests
export const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
